import { ChangeDetectionStrategy, Component, effect, inject, input, signal, viewChild } from '@angular/core';
import 'chartjs-adapter-moment';
import { CrosshairPlugin } from '../plugins/chartjs-plugin-crosshair.esm';
import { pluginTrendlineLinear } from '../plugins/chartjs-plugin-trendline.esm';
import { interpolate } from '../plugins/interpolate';
import { TranslateService } from '@ngx-translate/core';
import { BaseChartDirective } from 'ng2-charts';
import { Chart, registerables, Interaction, InteractionModeFunction } from 'chart.js';
import { MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MenuItem } from 'primeng/api';
import { ChartOptionsButtonComponent } from '../chart-options/chart-options-button.component';
import { ChartOptionsConfig } from '../chart-options/chart-options-storage.service';
import { applyYAxisMode } from '../chart-options/apply-y-axis-mode';
import { ChartLegendComponent } from '../chart-legend/chart-legend.component';
import { HtmlLegendItem, HtmlLegendPlugin } from '../chart-legend/chartjs-plugin-html-legend';

Chart.register(...registerables);

declare module 'chart.js' {
  interface InteractionModeMap {
    interpolate: InteractionModeFunction;
  }
}

Interaction.modes.interpolate = interpolate;

@Component({
  selector: 'cosmos-scatter-chart',
  imports: [BaseChartDirective, MenuModule, ButtonModule, TooltipModule, ChartOptionsButtonComponent, ChartLegendComponent],
  templateUrl: './scatter-chart.component.html',
  styles: [`
    :host {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
      min-width: 0;
    }
    .chart-wrapper {
      flex-grow: 1;
      position: relative;
      width: 100%;
      min-width: 0;
      height: 0;        /* flex-grow overrides this; it just gives a definite base */
      min-height: 450px; /* Chart.js needs a non-zero offsetHeight to measure */
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScatterChartComponent {
  private readonly translateService = inject(TranslateService);
  private readonly chart = viewChild(BaseChartDirective);

  readonly dataObjects = input<any[]>([]);
  readonly dataset = input<string>('');
  readonly params = input<any>({});
  readonly reorder = input(false);
  readonly xAxisType = input('linear');
  readonly xLabel = input('');
  readonly trendline = input<false | 'logarithmic' | 'linear'>(false);
  readonly sameOriginAllowed = input(false);
  readonly downloadAllowed = input(false);

  /** Stable id for the per-chart options button/localStorage persistence.
   *  Omit to not render the options button at all. */
  readonly chartId = input<string | undefined>(undefined);

  sameOrigin = signal(false);

  readonly downloadMenuItems: MenuItem[];

  readonly chartType = 'scatter';
  // HtmlLegendPlugin no-ops unless chartOptions.plugins.htmlLegend is set.
  readonly chartPlugins: any[] = [CrosshairPlugin, HtmlLegendPlugin];
  chartData: { datasets: any[]; labels: any[] } = { datasets: [], labels: [] };
  chartOptions: any;

  /** Current legend items — HtmlLegendPlugin (afterUpdate) refreshes this
   *  whenever chart data/visibility changes; ChartLegendComponent renders it. */
  legendItems: HtmlLegendItem[] = [];

  /** Current Y-axis display preference from ChartOptionsButtonComponent.
   *  Defaults to auto-data until the button reports a resolved (possibly
   *  stored) config on init. Scatter charts have no threshold-bands concept,
   *  so 'auto-bands' is never offered (hasBands is always false below). */
  private _yAxisConfig: ChartOptionsConfig = { yAxisMode: 'auto-data' };

  /** Persisted "reverse dataset order" toggle — applied inside initData()
   *  itself so it survives later rebuilds instead of reverting to source
   *  order (same reasoning as MultiLineChartComponent's _reversed). */
  private _reversed = false;

  constructor() {
    this.downloadMenuItems = [
      {
        label: this.translateService.instant('measurement.chart.download-png'),
        icon: 'fa-solid fa-file-image',
        command: () => this.downloadChart(),
      },
      {
        label: this.translateService.instant('measurement.chart.download-csv'),
        icon: 'fa-solid fa-file-csv',
        command: () => this.downloadCSV(),
      },
    ];

    effect(() => {
      // Re-run whenever any reactive input changes
      const trendline = this.trendline();
      if (trendline !== false && !this.chartPlugins.includes(pluginTrendlineLinear)) {
        this.chartPlugins.push(pluginTrendlineLinear);
      }
      this.initData();
    });
  }

  initData() {
    // Same-origin (relative elapsed time from each series' own start) only
    // makes sense for a time-based X axis — matches multi-line-chart's
    // relative-time transform, ported here since it was previously
    // unimplemented (sameOrigin was flipped but never actually applied).
    const useSameOrigin = this.sameOrigin() && this.xAxisType() === 'time';

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: useSameOrigin
          ? { type: 'linear', title: { display: true, text: '' }, ticks: { major: { enabled: true } } }
          : { type: this.xAxisType(), title: { display: true, text: this.xLabel() }, ticks: { major: { enabled: true } } },
        y: { type: 'linear', ticks: {} },
      },
      plugins: {
        tooltip: { enabled: true, mode: 'point', intersect: true },
        crosshair: { zoom: { enabled: false } },
        legend: { display: false },
        htmlLegend: { onLabelsChange: (items: HtmlLegendItem[]) => { this.legendItems = items; } },
      },
    };

    const rawSeries: { time: any[]; y: any[]; label: string }[] = [];

    for (const dataObject of this.dataObjects()) {
      if (Array.isArray(dataObject[this.dataset()])) {
        const time = dataObject.time.map((e: any) =>
          this.xAxisType() === 'time' ? new Date(e).getTime() : e
        );
        rawSeries.push({
          time,
          y: dataObject[this.dataset()],
          label: dataObject.name + ' - ' + this.translateService.instant('chart.legend.' + this.dataset()),
        });
      }
    }

    if (useSameOrigin) {
      // Auto-pick hours vs days based on the longest series' span, then
      // shift each series' own X values by its own first timestamp.
      let dateDivider = 1000 * 60 * 60;
      let axisText = 'Horas desde el inicio - Relativas';
      for (const series of rawSeries) {
        if (!series.time.length) continue;
        const spanDays = (series.time[series.time.length - 1] - series.time[0]) / (1000 * 60 * 60 * 24);
        if (spanDays >= 1) {
          dateDivider = 1000 * 60 * 60 * 24;
          axisText = 'Días desde el inicio - Relativos';
        }
      }
      this.chartOptions.scales.x.title.text = axisText;

      for (const series of rawSeries) {
        const zeroTime = series.time[0] ?? 0;
        series.time = series.time.map((t) => (t - zeroTime) / dateDivider);
      }
    }

    if (this._reversed) rawSeries.reverse();

    const datasets = rawSeries.map((series) => this.generateDataset(series.time, series.y, series.label));

    const params = this.params();
    // Scatter charts have no threshold-bands concept — bandConfig is always
    // null, so 'auto-bands' mode (never offered here) has nothing to do.
    applyYAxisMode(this.chartOptions.scales.y, this._yAxisConfig, null, params['suggestedMin'], params['suggestedMax']);

    // Replace the entire object so ng2-charts detects the reference change
    // and so Chart.js measures the container height correctly after it becomes visible
    this.chartData = { datasets, labels: [] };
  }

  generateDataset(dataX: any[], dataY: any[], label: string, color?: any, type?: string) {
    const data = dataX.map((x: any, i: number) => {
      if (this.xAxisType() === 'linear') return { x: Number(x), y: Number(dataY[i]) };
      return { x, y: Number(dataY[i]) };
    });

    const dataset: any = {
      fill: false,
      pointRadius: dataY.length > 480 ? 0 : 2,
      label,
      data,
    };

    if (this.trendline() === 'logarithmic') {
      dataset['trendlineLogarithmic'] = {
        colorMin: '#e74c3c',
        lineStyle: 'dotted',
        width: 0.5,
        label: { display: false, text: '', displayValue: true },
      };
    }

    if (type === 'threshold') dataset['borderDash'] = [5, 5];

    return dataset;
  }

  reorderDatasets() {
    // Post-hoc reversing the already-built chartData.datasets array doesn't
    // reliably repaint (Chart.js caches per-dataset-index draw state) — go
    // through initData()'s normal build path instead, same fix as
    // MultiLineChartComponent's reorderDatasets().
    this._reversed = !this._reversed;
    this.initData();
  }

  /** ChartOptionsButtonComponent (configChange) handler. */
  onChartOptionsChange(config: ChartOptionsConfig): void {
    this._yAxisConfig = config;
    this.sameOrigin.set(!!config.sameOrigin);
    this.initData();
  }

  /** ChartLegendComponent (toggle) handler — click a legend item to show/hide
   *  that dataset (see MultiLineChartComponent.onLegendToggle). */
  onLegendToggle(datasetIndex: number): void {
    const chart = this.chart();
    if (!chart) return;
    chart.hideDataset(datasetIndex, !chart.isDatasetHidden(datasetIndex));
  }

  downloadChart() {
    const chartElement = document.getElementById('comparechart-' + this.dataset()) as HTMLCanvasElement;
    const link = document.createElement('a');
    link.download = 'chart-' + this.dataset() + '-' + new Date().getTime() + '.png';
    link.href = chartElement.toDataURL('image/png');
    link.click();
  }

  downloadCSV() {
    const csvContent = this.generateCSV();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `data-${this.dataset()}-${new Date().getTime()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  private generateCSV(): string {
    const headers: string[] = [];
    const rows: string[] = [];

    for (const ds of this.chartData.datasets) {
      const baseName = ds.label.split(' - ')[0];
      headers.push(`${baseName}-time`, `${baseName}-${this.dataset()}`);
    }

    const maxPoints = Math.max(...this.chartData.datasets.map((d: any) => d.data.length));

    for (let i = 0; i < maxPoints; i++) {
      const row: any[] = [];
      for (const ds of this.chartData.datasets) {
        const point = ds.data[i];
        if (point) {
          row.push(`"${point.x.toISOString()}"`, point.y);
        } else {
          row.push('', '');
        }
      }
      rows.push(row.join(','));
    }

    return [headers.join(','), ...rows].join('\n');
  }
}
